import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Block as BlockIcon,
  Email as EmailIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import PageHeader from '../components/PageHeader';

type Audience = 'delivery_payers' | 'provider_clients' | 'handover_claimants';

const DEFAULT_SUBJECT = '💛 Vous avez retrouvé votre objet grâce à Sherlook !';

/** Le « Bonjour » est déjà ajouté par le template e-mail. */
const DEFAULT_BODY = `Vous vous souvenez de votre objet perdu au festival [[partenaire]] ?

Grâce à Sherlook, vous avez pu le retrouver simplement et rapidement. 💛

Cette année, grâce à Sherlook et aux équipes de [[partenaire]], des centaines de festivaliers ont pu retrouver leur objet perdu, pendant, mais aussi, après le festival grâce à notre livraison sécurisée partout en France !

Pour nous, chaque objet retrouvé est une petite victoire… et surtout un grand sourire retrouvé. 😊

💛 Votre avis compte énormément pour nous

Si vous avez apprécié votre expérience avec Sherlook, pourriez-vous prendre quelques secondes pour partager votre avis sur Google ?

⭐ Quelques secondes suffisent pour le partager sur Google.
Votre témoignage nous aide à faire connaître Sherlook à d'autres personnes qui, demain, pourraient avoir besoin de retrouver un objet perdu.

[[button]]

Et Sherlook, ce n'est pas seulement pendant les festivals !

Sherlook est aussi là dans votre vie de tous les jours.
Vous trouvez un téléphone, des clés, un portefeuille ou un autre objet ? Plutôt que de devoir chercher où le déposer alors que vous manquez de temps, publiez-le simplement sur Sherlook et donnez une chance à son propriétaire de le retrouver.

Et si c'est vous qui perdez quelque chose : ayez le réflexe Sherlook !
Publiez votre annonce et laissez notre plateforme vous aider à retrouver votre objet plus facilement.

Un geste simple pour celui qui trouve, un immense bonheur pour celui qui retrouve. 💛

👉 Rien n'est jamais perdu ! Retrouvez votre sourire avec Sherlook.

Merci pour votre confiance ❤️
L'équipe Sherlook
Le site des objets trouvés et retrouvés`;

const DEFAULT_CTA_URL =
  'https://search.google.com/local/writereview?placeid=ChIJbXDSLh_fDkgRA4kR3E7i6jg';
const DEFAULT_CTA_LABEL = 'Je laisse mon avis sur Google';

const SatisfactionEmailsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [audience, setAudience] = useState<Audience>('delivery_payers');
  const [providerId, setProviderId] = useState<number | ''>('');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [ctaUrl, setCtaUrl] = useState(DEFAULT_CTA_URL);
  const [ctaLabel, setCtaLabel] = useState(DEFAULT_CTA_LABEL);
  const [testRecipient, setTestRecipient] = useState('odoreille@gmail.com');
  const [forceResend, setForceResend] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [banSearch, setBanSearch] = useState('');
  const [banSearchDebounced, setBanSearchDebounced] = useState('');
  const [banSearchProviderId, setBanSearchProviderId] = useState<number | ''>('');
  const [banListFilter, setBanListFilter] = useState('');
  const [manualBanEmail, setManualBanEmail] = useState('');
  const [manualBanName, setManualBanName] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setBanSearchDebounced(banSearch.trim()), 300);
    return () => window.clearTimeout(t);
  }, [banSearch]);

  const providersQuery = useQuery({
    queryKey: ['admin-satisfaction-providers'],
    queryFn: () => apiService.getSatisfactionEmailProviders(),
  });

  const bansQuery = useQuery({
    queryKey: ['admin-satisfaction-bans', banListFilter],
    queryFn: () => apiService.getSatisfactionEmailBans({ q: banListFilter || undefined, limit: 100 }),
  });

  const candidatesQuery = useQuery({
    queryKey: ['admin-satisfaction-ban-candidates', banSearchDebounced, banSearchProviderId],
    queryFn: () =>
      apiService.searchSatisfactionEmailBanCandidates({
        q: banSearchDebounced,
        limit: 30,
        location_provider_id: banSearchProviderId === '' ? undefined : banSearchProviderId,
      }),
    enabled: banSearchDebounced.length >= 2,
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      apiService.previewSatisfactionEmailAudience({
        audience,
        location_provider_id: providerId === '' ? undefined : providerId,
        force: forceResend,
      }),
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setFeedback({ type: 'error', message: msg || "Impossible de prévisualiser l'audience." });
    },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiService.sendSatisfactionEmailTest({
        recipient: testRecipient.trim(),
        subject: subject.trim(),
        body: body.trim(),
        cta_url: ctaUrl.trim() || undefined,
        cta_label: ctaLabel.trim() || undefined,
        location_provider_id: providerId === '' ? undefined : providerId,
      }),
    onSuccess: (res) => setFeedback({ type: 'success', message: res.message }),
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setFeedback({ type: 'error', message: msg || "Échec de l'envoi test." });
    },
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      apiService.sendSatisfactionEmailCampaign({
        audience,
        location_provider_id: providerId === '' ? undefined : providerId,
        subject: subject.trim(),
        body: body.trim(),
        cta_url: ctaUrl.trim() || undefined,
        cta_label: ctaLabel.trim() || undefined,
        force: forceResend,
      }),
    onSuccess: (res) => {
      setConfirmOpen(false);
      setFeedback({ type: res.ok ? 'success' : 'error', message: res.message });
      previewMutation.mutate();
    },
    onError: (e: unknown) => {
      setConfirmOpen(false);
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setFeedback({ type: 'error', message: msg || 'Échec de la campagne.' });
    },
  });

  const banMutation = useMutation({
    mutationFn: (payload: { email: string; display_name?: string }) =>
      apiService.banSatisfactionEmail(payload),
    onSuccess: (res) => {
      setFeedback({
        type: 'success',
        message: res.created
          ? `${res.email} ne recevra plus d'e-mails de satisfaction.`
          : `${res.email} est déjà banni.`,
      });
      setManualBanEmail('');
      setManualBanName('');
      void queryClient.invalidateQueries({ queryKey: ['admin-satisfaction-bans'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-satisfaction-ban-candidates'] });
      previewMutation.reset();
    },
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setFeedback({ type: 'error', message: msg || 'Impossible de bannir.' });
    },
  });

  const unbanMutation = useMutation({
    mutationFn: (id: number) => apiService.unbanSatisfactionEmail(id),
    onSuccess: () => {
      setFeedback({ type: 'success', message: 'Bannissement retiré.' });
      void queryClient.invalidateQueries({ queryKey: ['admin-satisfaction-bans'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-satisfaction-ban-candidates'] });
      previewMutation.reset();
    },
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setFeedback({ type: 'error', message: msg || 'Impossible de retirer le ban.' });
    },
  });

  const preview = previewMutation.data;
  const providerRequired = audience === 'provider_clients';
  const canPreview = !providerRequired || providerId !== '';
  const canSend =
    canPreview &&
    subject.trim() !== '' &&
    body.trim() !== '' &&
    (preview?.deliverable ?? 0) > 0;

  const audienceHelp = useMemo(() => {
    if (audience === 'delivery_payers') {
      return 'Personnes qui ont payé une livraison (Colissimo / Chronopost / Mondial Relay / UPS). Filtre partenaire optionnel.';
    }
    if (audience === 'handover_claimants') {
      return 'Personnes ayant récupéré un objet en main propre, avec e-mail saisi à la restitution (claimantEmail). Filtre partenaire optionnel.';
    }
    return 'Particuliers ayant échangé (conversation) sur une annonce ou un objet trouvé du partenaire sélectionné. Les comptes staff du partenaire sont exclus.';
  }, [audience]);

  return (
    <Box>
      <PageHeader
        title={
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Emails satisfaction
          </Typography>
        }
        subtitle={
          <Typography variant="body2" color="text.secondary">
            Envoi d&apos;un e-mail personnalisé avec en-tête et pied de page Sherlook. Tags :{' '}
            <code>{'{{prenom}}'}</code>, <code>{'[[partenaire]]'}</code>, <code>{'[[button]]'}</code>
          </Typography>
        }
      />

      {feedback && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BlockIcon fontSize="small" />
              Personnes bannies
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Les adresses bannies ne reçoivent jamais d&apos;e-mail de satisfaction (campagne, forçage ou test).
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems={{ sm: 'flex-start' }}>
              <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360 }}>
                <InputLabel>Partenaire (recherche)</InputLabel>
                <Select
                  label="Partenaire (recherche)"
                  value={banSearchProviderId === '' ? '' : String(banSearchProviderId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBanSearchProviderId(v === '' ? '' : Number(v));
                  }}
                >
                  <MenuItem value="">Tous les partenaires</MenuItem>
                  {(providersQuery.data?.providers ?? []).map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                label="Rechercher (nom, e-mail ou n° d'objet)"
                value={banSearch}
                onChange={(e) => setBanSearch(e.target.value)}
                sx={{ maxWidth: 420 }}
                helperText="Nom, e-mail, ou n° d'étiquette (ex. T12-45 ou 45). Filtre partenaire recommandé pour les n°."
              />
            </Stack>

            {banSearchDebounced.length >= 2 && (
              <Box sx={{ mb: 2 }}>
                {candidatesQuery.isFetching ? (
                  <CircularProgress size={20} />
                ) : (candidatesQuery.data?.candidates ?? []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Aucun résultat (utilisateur ou objet trouvé).
                  </Typography>
                ) : (
                  <List dense sx={{ maxHeight: 220, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                    {(candidatesQuery.data?.candidates ?? []).map((row) => {
                      const name = [row.first_name, row.last_name].filter(Boolean).join(' ');
                      const refLabel = row.reference_code
                        ? `objet ${row.reference_code}`
                        : row.found_object_id
                          ? `objet #${row.found_object_id}`
                          : null;
                      const secondaryParts = [
                        row.id > 0 ? `#${row.id}` : null,
                        name || null,
                        refLabel,
                      ].filter(Boolean);
                      return (
                        <ListItem key={`${row.email}-${row.found_object_id ?? 'u'}-${row.id}`} divider>
                          <ListItemText
                            primary={row.email}
                            secondary={secondaryParts.join(' — ') || undefined}
                          />
                          <ListItemSecondaryAction>
                            {row.banned ? (
                              <Chip size="small" label="Banni" color="error" variant="outlined" />
                            ) : (
                              <Button
                                size="small"
                                color="error"
                                disabled={banMutation.isPending}
                                onClick={() =>
                                  banMutation.mutate({
                                    email: row.email,
                                    display_name: name || undefined,
                                  })
                                }
                              >
                                Bannir
                              </Button>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'flex-start' }}>
              <TextField
                size="small"
                label="E-mail à bannir"
                value={manualBanEmail}
                onChange={(e) => setManualBanEmail(e.target.value)}
                sx={{ minWidth: 260 }}
              />
              <TextField
                size="small"
                label="Nom (optionnel)"
                value={manualBanName}
                onChange={(e) => setManualBanName(e.target.value)}
                sx={{ minWidth: 180 }}
              />
              <Button
                variant="outlined"
                color="error"
                disabled={banMutation.isPending || manualBanEmail.trim() === ''}
                onClick={() =>
                  banMutation.mutate({
                    email: manualBanEmail.trim(),
                    display_name: manualBanName.trim() || undefined,
                  })
                }
              >
                Bannir
              </Button>
            </Stack>

            <TextField
              size="small"
              label="Filtrer la liste des bannis"
              value={banListFilter}
              onChange={(e) => setBanListFilter(e.target.value)}
              sx={{ maxWidth: 360, mb: 1 }}
            />

            {bansQuery.isLoading ? (
              <CircularProgress size={20} />
            ) : (bansQuery.data?.bans ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aucune personne bannie.
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 260, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                {(bansQuery.data?.bans ?? []).map((ban) => (
                  <ListItem key={ban.id} divider>
                    <ListItemText
                      primary={ban.email}
                      secondary={
                        ban.display_name
                          ? `${ban.display_name} — depuis ${new Date(ban.created_at).toLocaleDateString('fr-FR')}`
                          : `Depuis ${new Date(ban.created_at).toLocaleDateString('fr-FR')}`
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="débannir"
                        disabled={unbanMutation.isPending}
                        onClick={() => unbanMutation.mutate(ban.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Destinataires
            </Typography>
            <RadioGroup
              value={audience}
              onChange={(e) => {
                setAudience(e.target.value as Audience);
                previewMutation.reset();
              }}
            >
              <FormControlLabel
                value="delivery_payers"
                control={<Radio />}
                label="Clients ayant reçu / payé une livraison"
              />
              <FormControlLabel
                value="handover_claimants"
                control={<Radio />}
                label="Restitutions en main propre (e-mail collecté)"
              />
              <FormControlLabel
                value="provider_clients"
                control={<Radio />}
                label="Clients d'un partenaire (conversations)"
              />
            </RadioGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {audienceHelp}
            </Typography>

            <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
              <InputLabel>Partenaire</InputLabel>
              <Select
                label="Partenaire"
                value={providerId === '' ? '' : String(providerId)}
                onChange={(e) => {
                  const v = e.target.value;
                  setProviderId(v === '' ? '' : Number(v));
                  previewMutation.reset();
                }}
              >
                <MenuItem value="">
                  {providerRequired ? 'Choisir un partenaire…' : 'Tous les partenaires'}
                </MenuItem>
                {(providersQuery.data?.providers ?? []).map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={previewMutation.isPending ? <CircularProgress size={16} /> : <PreviewIcon />}
                disabled={!canPreview || previewMutation.isPending}
                onClick={() => {
                  setFeedback(null);
                  previewMutation.mutate();
                }}
              >
                Prévisualiser l&apos;audience
              </Button>
              {preview && (
                <>
                  <Chip label={`${preview.deliverable} destinataire(s) délivrables`} color="primary" />
                  <Chip label={`${preview.skipped_no_email} sans e-mail`} variant="outlined" />
                  {(preview.skipped_already_sent ?? 0) > 0 ? (
                    <Chip
                      label={`${preview.skipped_already_sent} déjà contacté(s)`}
                      color={forceResend ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  ) : null}
                  {(preview.skipped_banned ?? 0) > 0 ? (
                    <Chip label={`${preview.skipped_banned} banni(s)`} color="error" variant="outlined" />
                  ) : null}
                  <Chip label={`${preview.total} au total`} variant="outlined" />
                </>
              )}
            </Stack>

            <FormControlLabel
              sx={{ mt: 2, display: 'block' }}
              control={
                <Checkbox
                  checked={forceResend}
                  onChange={(e) => {
                    setForceResend(e.target.checked);
                    previewMutation.reset();
                  }}
                />
              }
              label="Forcer le renvoi aux destinataires déjà contactés"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Sans forçage, les e-mails déjà envoyés (campagne) sont exclus. Les envois de test ne comptent pas. Les
              bannis restent exclus même avec forçage.
            </Typography>

            {preview && preview.sample.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Échantillon (max 30)
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                  {preview.sample.map((row) => (
                    <ListItem key={row.id} divider>
                      <ListItemText
                        primary={row.email}
                        secondary={`#${row.id}${row.first_name ? ` — ${row.first_name}` : ''}${row.last_name ? ` ${row.last_name}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contenu
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Objet"
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <TextField
                label="Texte"
                fullWidth
                multiline
                minRows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                helperText="Le « Bonjour » + prénom est ajouté automatiquement. Tags : [[partenaire]] (nom du partenaire sélectionné), [[button]] (bouton CTA)."
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="URL du bouton"
                  fullWidth
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                />
                <TextField
                  label="Libellé du bouton"
                  fullWidth
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Envoi
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                size="small"
                label="E-mail de test"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Button
                variant="outlined"
                disabled={testMutation.isPending || subject.trim() === '' || body.trim() === ''}
                onClick={() => {
                  setFeedback(null);
                  testMutation.mutate();
                }}
              >
                {testMutation.isPending ? 'Envoi…' : 'Envoyer un test'}
              </Button>
              <Box flexGrow={1} />
              <Button
                variant="contained"
                color="primary"
                startIcon={sendMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={!canSend || sendMutation.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                Envoyer la campagne
              </Button>
            </Stack>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Maximum 500 destinataires par envoi. Respecte les quotas SMTP (pause anti-spam). Vérifiez toujours
              l&apos;aperçu avant l&apos;envoi.
            </Alert>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmer l&apos;envoi</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Envoyer « {subject.trim()} » à <strong>{preview?.deliverable ?? 0}</strong> destinataire(s)
            {audience === 'provider_clients' && providerId !== ''
              ? ` (partenaire #${providerId})`
              : audience === 'delivery_payers'
                ? ' (payeurs livraison)'
                : audience === 'handover_claimants'
                  ? ' (restitutions main propre)'
                  : ''}
            ?
            {forceResend ? (
              <>
                <br />
                <strong>Forçage activé</strong> : les destinataires déjà contactés seront renvoyés (sauf bannis).
              </>
            ) : (preview?.skipped_already_sent ?? 0) > 0 ? (
              <>
                <br />
                {(preview?.skipped_already_sent ?? 0)} destinataire(s) déjà contacté(s) seront ignorés.
              </>
            ) : null}
            {(preview?.skipped_banned ?? 0) > 0 ? (
              <>
                <br />
                {(preview?.skipped_banned ?? 0)} destinataire(s) banni(s) seront exclus.
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            Confirmer l&apos;envoi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SatisfactionEmailsPage;
